import {
    PROJECT_NAME,
    INSCRIPTION_EXPENSE_INCOME,
    BUDGET_NAME_FIELD,
    NATURE_FIELD,
    ORG_CHART_FINANCE_CODE_FIELD,
    INSCRIPTION_INVESTMENT_OPERATING,
    CHAPTER_FIELD,
    OPERATION_FIELD,
    ORG_CHART_FINANCE_FIELD,
    GESTIONNAIRE_CODE_FIELD,
    NATURE_LABEL_FIELD,
    FUNCTION_FIELD,
    ANTENNA_FIELD,
    GESTIONNAIRE_FIELD,
    ANALYTIC_NAME_FIELD,
    BUDGET_LINE_CODE,
    SUPER_GESTIONNAIRE_CODE_FIELD,
    SUPER_GESTIONNAIRE_FIELD,
    BUDGET_LINE_PROGRAM_NAME,
    BUDGET_LINE_PROGRAM_CODE,
    ENVELOPPE_CODE,
    ENVELOPPE_LABEL,
    ANALYTIC_NATURE_CODE,
    ANALYTIC_NATURE_NAME,
    DIRECTION_CODE,
    DIRECTION_LABEL,
} from 'routes/BudgetModule/config/constants';
import { translateBlockValue } from 'i18n';
import { BudgetExpenseOrIncome } from 'business/constants';
import { PivotFieldsAggridT } from 'business/definitions';
import {
    BudgetProposalSettingsT,
    BudgetProposalT,
} from 'routes/BudgetModule/config/types';
import { t } from 'i18next';
import { ColDef } from 'ag-grid-community';
import { budgetCurrencyFormatter } from 'ui/AgGridReact/formatters';

export const getProjectGridDimensions = (
    integratedSoftwares: Set<string> | undefined,
): string[] => {
    /* Return the project grid dimensions adapted to specific softwares cases */

    // TODO : This entire method is not meant to be a long term solution. We should instead use some kind of polymorphism
    // in order to build a better multiple-software management in budget.
    if (integratedSoftwares?.has('SIFAC')) {
        return [
            BUDGET_NAME_FIELD,
            BUDGET_LINE_CODE,
            ANALYTIC_NAME_FIELD,
            ORG_CHART_FINANCE_CODE_FIELD,
            ORG_CHART_FINANCE_FIELD,
            CHAPTER_FIELD,
            OPERATION_FIELD,
        ];
    }

    if (integratedSoftwares?.has('GRAND_ANGLE')) {
        return [
            BUDGET_NAME_FIELD,
            INSCRIPTION_INVESTMENT_OPERATING,
            GESTIONNAIRE_CODE_FIELD,
            GESTIONNAIRE_FIELD,
            BUDGET_LINE_PROGRAM_CODE,
            BUDGET_LINE_PROGRAM_NAME,
            ENVELOPPE_CODE,
            ENVELOPPE_LABEL,
            ANALYTIC_NATURE_CODE,
            ANALYTIC_NATURE_NAME,
            ORG_CHART_FINANCE_FIELD,
            ORG_CHART_FINANCE_CODE_FIELD,
            NATURE_FIELD,
            NATURE_LABEL_FIELD,
            FUNCTION_FIELD,
            CHAPTER_FIELD,
            ANTENNA_FIELD,
            OPERATION_FIELD,
        ];
    }

    if (integratedSoftwares?.has('CIRIL_FINANCES')) {
        return [
            BUDGET_NAME_FIELD,
            INSCRIPTION_INVESTMENT_OPERATING,
            GESTIONNAIRE_CODE_FIELD,
            GESTIONNAIRE_FIELD,
            SUPER_GESTIONNAIRE_CODE_FIELD,
            SUPER_GESTIONNAIRE_FIELD,
            ORG_CHART_FINANCE_FIELD,
            ORG_CHART_FINANCE_CODE_FIELD,
            NATURE_FIELD,
            NATURE_LABEL_FIELD,
            FUNCTION_FIELD,
            CHAPTER_FIELD,
            ANTENNA_FIELD,
            OPERATION_FIELD,
        ];
    }
    if (integratedSoftwares?.has('SEDIT_FINANCES')) {
        return [
            BUDGET_NAME_FIELD,
            INSCRIPTION_INVESTMENT_OPERATING,
            GESTIONNAIRE_CODE_FIELD,
            GESTIONNAIRE_FIELD,
            DIRECTION_CODE,
            DIRECTION_LABEL,
            ORG_CHART_FINANCE_FIELD,
            ORG_CHART_FINANCE_CODE_FIELD,
            NATURE_FIELD,
            NATURE_LABEL_FIELD,
            FUNCTION_FIELD,
            CHAPTER_FIELD,
            ANTENNA_FIELD,
            OPERATION_FIELD,
        ];
    }

    return [
        BUDGET_NAME_FIELD,
        INSCRIPTION_INVESTMENT_OPERATING,
        GESTIONNAIRE_CODE_FIELD,
        GESTIONNAIRE_FIELD,
        ORG_CHART_FINANCE_FIELD,
        ORG_CHART_FINANCE_CODE_FIELD,
        NATURE_FIELD,
        NATURE_LABEL_FIELD,
        FUNCTION_FIELD,
        CHAPTER_FIELD,
        ANTENNA_FIELD,
        OPERATION_FIELD,
    ];
};

export const getInitialProjectsPivotGridColDefs = (
    amountRequestedField: string,
    budgetProposal: BudgetProposalT,
    baseFilters: QueryFilterT[],
    onInscriptionsModalClosed: (arg1: void) => void,
): ColDef[] => [
    {
        aggFunc: 'sum',
        enablePivot: true,
        enableRowGroup: true,
        field: amountRequestedField,
        filter: 'agTextColumnFilter',
        hide: false,
        pivot: false,
        pivotIndex: null,
        rowGroup: false,
        rowGroupIndex: 0,
        sortable: true,
        valueFormatter: budgetCurrencyFormatter,
        cellRenderer: 'amountWithInscriptionsButtonCellRenderer',
        cellRendererParams: {
            baseFilters,
            budgetProposal,
            onInscriptionsModalClosed,
        },
    },
    {
        aggFunc: null,
        enablePivot: true,
        enableRowGroup: true,
        field: PROJECT_NAME,
        filter: 'agTextColumnFilter',
        hide: false,
        pivot: false,
        pivotIndex: null,
        rowGroup: true,
        rowGroupIndex: 0,
        sort: 'asc',
        sortable: true,
        valueFormatter: ({ value }) =>
            value === 'unknown' ? t('cellar.blanks') : value,
    },
    {
        aggFunc: null,
        enablePivot: true,
        enableRowGroup: true,
        field: INSCRIPTION_EXPENSE_INCOME,
        filter: 'agTextColumnFilter',
        hide: false,
        pivot: true,
        pivotIndex: null,
        rowGroup: false,
        rowGroupIndex: 0,
        sort: 'asc',
        sortable: true,
        valueFormatter: ({ value }) => translateBlockValue(value, true),
    },
];

export const generateGetDimensionColumn =
    (visibleDimensionsByDefault: string[]): ((dimension: string) => ColDef) =>
    (dimension: string) => {
        const rowGroupIndex = visibleDimensionsByDefault.indexOf(dimension);

        return {
            aggFunc: null,
            enablePivot: true,
            enableRowGroup: true,
            field: dimension,
            filter: 'agTextColumnFilter',
            hide: false,
            pivot: false,
            pivotIndex: null,
            rowGroup: rowGroupIndex !== -1,
            rowGroupIndex: rowGroupIndex === -1 ? null : rowGroupIndex,
            sort: 'asc',
            sortable: true,
            valueFormatter: ({ value }: { value: string }) =>
                value === 'unknown' ? t('cellar.blanks') : value,
        };
    };

export const getPivotGridMutationBody = (
    expenseOrIncome: BudgetExpenseOrIncome,
    settings: PivotFieldsAggridT,
): Partial<BudgetProposalSettingsT> =>
    expenseOrIncome === BudgetExpenseOrIncome.EXPENSE
        ? {
              analyseExpenseGrid: {
                  columns: settings,
              },
          }
        : {
              analyseIncomeGrid: {
                  columns: settings,
              },
          };
